import type { Product, ProductVariant } from "@/mocks/products";
import { normalizeText } from "@/utils/collections";

// "Off White / P" a partir dos valores da variação ({ Cor: "Off White", Tamanho: "P" }).
export function variantLabel(values: Record<string, string> | undefined): string | undefined {
  const parts = Object.values(values ?? {}).filter(Boolean);
  return parts.length > 0 ? parts.join(" / ") : undefined;
}

// Foto da variação (a que o lojista vinculou na Yampi); sem foto própria,
// usa a foto principal do produto.
export function variantImage(product: Product, variant: ProductVariant | null | undefined): string {
  if (variant?.imageId && product.imageIds && product.images) {
    const index = product.imageIds.indexOf(variant.imageId);
    if (index >= 0 && product.images[index]) return product.images[index];
  }
  return product.image;
}

export function defaultVariant(product: Product): ProductVariant | undefined {
  return product.variants?.find((entry) => entry.id === product.variantId);
}

// Quantidade máxima que dá pra comprar da variação (estoque), ou undefined se a
// loja não controla estoque dela.
export function variantMaxQuantity(variant: ProductVariant | null | undefined): number | undefined {
  return typeof variant?.stock === "number" ? Math.max(0, variant.stock) : undefined;
}

// Nome do atributo de tamanho e de cor, como o lojista cadastrou ("Tamanho", "Size", "Cor"...).
export function sizeAttribute(product: Product): string | undefined {
  return Object.keys(product.attributes ?? {}).find((name) => /tamanho|size/i.test(name));
}

export function colorAttribute(product: Product): string | undefined {
  return Object.keys(product.attributes ?? {}).find((name) => /^(cor|cores|color)$/i.test(name.trim()));
}

// Variação de um tamanho, mantendo a cor da variação padrão quando ela existe nesse tamanho.
export function variantForSize(product: Product, attribute: string, size: string): ProductVariant | undefined {
  const base = defaultVariant(product);
  const matches = (product.variants ?? []).filter((entry) => entry.values[attribute] === size && entry.inStock);
  const sameOptions = matches.find((entry) =>
    Object.entries(base?.values ?? {}).every(([name, value]) => name === attribute || entry.values[name] === value)
  );
  return sameOptions ?? matches[0];
}

// Variação em estoque para o tamanho e a cor escolhidos no cartão.
export function variantForChoice(product: Product, sizeName: string, size: string, colorName?: string, color?: string): ProductVariant | undefined {
  if (!colorName || !color) return variantForSize(product, sizeName, size);
  return product.variants?.find((entry) => entry.inStock && entry.values[sizeName] === size && entry.values[colorName] === color);
}

const COLOR_HEX: Record<string, string> = {
  preto: "#1f1c1a",
  branco: "#ffffff",
  "off white": "#efe9dd",
  "off-white": "#efe9dd",
  offwhite: "#efe9dd",
  bege: "#d8c6ab",
  nude: "#c9a27e",
  caramelo: "#a9743a",
  marrom: "#5c4030",
  azul: "#26365f",
  "azul marinho": "#1d2a4a",
  marinho: "#1d2a4a",
  cinza: "#8f8f8f",
  verde: "#55684f",
  vermelho: "#8f2b28",
  laranja: "#d9702c",
  rosa: "#d6417f",
  "rosa pink": "#d6417f",
  pink: "#d6417f",
};

// Cor da amostra; undefined quando o nome cadastrado não é conhecido (a loja mostra só o texto).
export function colorHex(name: string): string | undefined {
  return COLOR_HEX[normalizeText(name)];
}
